import React, { useCallback, useEffect, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import styles from "./layout.module.css";

const Editor = ({ key, selectedItem, updatePosition, editorRef }) => {
  const selectedIdRef = useRef(selectedItem?.id);
  selectedIdRef.current = selectedItem?.id;

  const updatePositionRef = useRef(updatePosition);
  updatePositionRef.current = updatePosition;

  // The last HTML this editor pushed up to the canvas. Typing feeds `items`,
  // and `items` feeds `selectedItem.content` right back here — comparing against
  // this ref tells our own echo apart from a genuine outside change (tag insert,
  // a different element selected), so we never re-seed Jodit mid-keystroke and
  // send the caret back to the start.
  const lastEmittedRef = useRef(selectedItem?.content ?? '');
  const [value, setValue] = useState(selectedItem?.content ?? '');

  useEffect(() => {
    const incoming = selectedItem?.content ?? '';
    if (incoming !== lastEmittedRef.current) {
      lastEmittedRef.current = incoming;
      setValue(incoming);
    }
  }, [selectedItem?.content]);

  const pushContent = useCallback((content) => {
    const id = selectedIdRef.current;
    if (id == null || content == null) return;
    if (content === lastEmittedRef.current) return;
    lastEmittedRef.current = content;
    updatePositionRef.current(id, { content });
  }, []);

  const [config] = useState({
    readonly: false,
    toolbarSticky: false,
    askBeforePasteHTML: false,
    defaultActionOnPaste: 'insert_only_text',
    processPasteHTML: false,
    statusbar: false,
    autofocus: true,
    toolbarInline: false,
    removeButtons: [
      'about',
      'strikethrough',
      'undo',
      'redo',
      'speechRecognize',
      'spellcheck',
      'dots'
    ],
    buttons: [
      'bold',
      'italic',
      'underline',
      'align',
      'link',
      'ul',
      'ol',
      'outdent',
      'indent',
      'font',
      'fontsize',
      'paragraph',
      'eraser',
      'brush'
    ],
    toolbarAdaptive: false,
    extraButtons: ['table'],
    events: {
      keydown: function (event) {
        if (event.key !== 'Backspace') return;
        try {
          const content = this?.value;
          if (content && content !== '<p><br></p>') {
            const tagRegex = /\{[^}]*$/g;
            content.match(tagRegex);
          }
        } catch (e) {}
      }
    },
  });

  const handleJoditReady = useCallback((jodit) => {
    if (editorRef) editorRef.current = jodit;
    if (!jodit || !jodit.events) return;

    const blurHandler = () => {
      try {
        pushContent(jodit.value);
      } catch (e) {}
    };

    jodit.events.on('blur', blurHandler);
  }, [editorRef, pushContent]);

  // Jodit fires 'change' as the user types (debounced by its own observer), so
  // the canvas element repaints live instead of waiting for blur.
  const handleChange = useCallback((content) => {
    try {
      pushContent(content);
    } catch (e) {}
  }, [pushContent]);

  return (
    <div className={styles.editor}>
      <div className={styles.editorField}>
        <JoditEditor
          key={selectedItem?.id}
          editorRef={handleJoditReady}
          config={config}
          value={value}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

export default Editor;
